/**
 * @file `<Glossary/>` — alphabetical-by-label render of a course's
 *       glossary terms.
 * @module @labcraft/course-kit/components/glossary
 *
 * Pure presentational component. Takes `terms` as a prop so the same
 * surface works for course-package-imported glossaries today and for
 * a future context-driven version (used by inline `<Term slug="…"/>`
 * tooltips) without re-authoring.
 *
 * Each entry renders with a stable `id="<slug>"` anchor so chapter
 * prose elsewhere in the course can link to entries via
 * `[*closure*](../../glossary#closure)`-style fragments.
 *
 * Term bodies support the inline mini-syntax {@link renderInline}
 * parses: `` `inline code` ``, `**bold**`, and `*italic*` / `_italic_`.
 * No block markdown and no links; the rest is plain text. Authors who
 * need richer formatting should split into multiple sentences or extend
 * the schema.
 */

import { useContext, type JSX, type ReactNode } from 'react'
import type { Term } from '../types.ts'
import { GlossaryContext } from './glossary_context.ts'
import styles from './glossary.module.css'

export interface GlossaryProps {
  /** Term entries to render. Optional: when omitted, the component reads
   *  the course glossary from {@link GlossaryContext} — the same context
   *  `<GlossaryProvider terms={course.glossary}>` populates for inline
   *  `<Term/>` tooltips — so a chapter can render `<Glossary/>` without
   *  importing the course's `GLOSSARY` (an MDX `import` the builder can't
   *  inline, which 404s the compiled module at runtime). An explicit
   *  `terms` prop still wins for non-reader callers. The component sorts a
   *  copy, so the source data's authoring order is preserved. */
  terms?: Term[]
}

export function Glossary(props: GlossaryProps): JSX.Element {
  const contextTerms = useContext(GlossaryContext)
  const terms = props.terms ?? contextTerms
  const sorted = [...terms].sort((a, b) => a.label.localeCompare(b.label))
  return (
    <dl className={styles.glossary} aria-label="Glossary">
      {sorted.map(term => (
        <div key={term.slug} className={styles.entry}>
          <dt id={term.slug} className={styles.label}>
            <a href={`#${term.slug}`} className={styles.anchor}>
              {term.label}
            </a>
          </dt>
          <dd className={styles.body}>
            <p className={styles.short}>{renderInline(term.short)}</p>
            <p>{renderInline(term.body)}</p>
            {term.seeAlso && term.seeAlso.length > 0 && (
              <p className={styles.seeAlso}>
                See also:{' '}
                {term.seeAlso.map((slug, i) => (
                  <span key={slug}>
                    {i > 0 && ', '}
                    <a href={`#${slug}`}>{slug}</a>
                  </span>
                ))}
              </p>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * The inline spans {@link renderInline} recognizes, tried left to right
 * at each position: a code span first (its inner text stays literal, so
 * a `*` inside backticks is never emphasis), then `**bold**`, then
 * `*italic*` or `_italic_`. The underscore form must sit on word
 * boundaries so a snake_case name in plain text stays literal.
 */
const INLINE_SPAN =
  /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*|(?<!\w)_([^_]+)_(?!\w)/g

/**
 * Render one line of authored inline markdown into ReactNode children:
 * `` `code` `` becomes `<code>`, `**bold**` `<strong>`, and `*italic*` /
 * `_italic_` `<em>`. Every span is a real React element, never injected
 * HTML. An unmatched marker (a lone backtick or asterisk) renders as
 * literal text, not silent truncation. Shared by the glossary, `<Term/>`'s
 * tooltip, and `<Objective/>`'s hints and command explanations, which are
 * all authored as plain strings with the same inline markdown the chapter
 * prose uses.
 *
 * @param text - the authored string
 * @returns the string parsed into text spans plus `code` / `strong` /
 *   `em` elements
 */
export function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(INLINE_SPAN)) {
    const at = m.index ?? 0
    if (at > last) {
      nodes.push(<span key={nodes.length}>{text.slice(last, at)}</span>)
    }

    const key = nodes.length
    if (m[1] !== undefined) nodes.push(<code key={key}>{m[1]}</code>)
    else if (m[2] !== undefined) nodes.push(<strong key={key}>{m[2]}</strong>)
    else nodes.push(<em key={key}>{m[3] ?? m[4]}</em>)
    last = at + m[0].length
  }

  if (last < text.length) {
    nodes.push(<span key={nodes.length}>{text.slice(last)}</span>)
  }
  return nodes
}
